import { PREVIEW_FILE_ROUTES } from 'config/routes'
import React from 'react'
import type { Booking } from 'repositories/BerthApi/types/booking'
import type { BookingMasterData } from 'repositories/BerthApi/getBookingMasterData/type'

import { BOOKING_STATUS } from 'constants/Booking'
import useAttachmentFileDownload from 'hooks/useAttachmentFileDownload'
import {
  canPreviewFileAttachment,
  determineIconOfFileName,
  extractExtensionOfFileName,
} from 'utils/commonFunctions'
import { formatPhoneNumber } from 'utils/formatPhoneNumber'

import { canEditBookingByStatus } from '../../utils/berthManagementHelpers'
import { LoadingAndUnLoadingBadge } from '../LoadingAndUnLoadingBadge'
import { StatusChip } from '../StatusChip'

import styles from './index.module.css'
import { calcDurationMinutes, formatTimeRange, getSortedImportExportFields } from './util'

const CHANGE_STATUS_BUTTON: Record<number, { label: string; value: number; className: string }> = {
  [BOOKING_STATUS.Booking]: {
    label: '予約済にする',
    value: BOOKING_STATUS.Confirmed,
    className: styles.btnBooking,
  },
  [BOOKING_STATUS.Recept]: {
    label: '呼出中にする',
    value: BOOKING_STATUS.Calling,
    className: styles.btnArrival,
  },
  [BOOKING_STATUS.Arrival]: {
    label: '呼出中にする',
    value: BOOKING_STATUS.Calling,
    className: styles.btnArrival,
  },
  [BOOKING_STATUS.Calling]: {
    label: '作業中にする',
    value: BOOKING_STATUS.Working,
    className: styles.btnCalling,
  },
  [BOOKING_STATUS.Working]: {
    label: '作業完了にする',
    value: BOOKING_STATUS.Finish,
    className: styles.btnWorking,
  },
}

type CallingAction = {
  label: string
  onClick: () => void
  disabled?: boolean
}

type Props = {
  booking: Booking
  bookingKey?: string
  masterData: BookingMasterData | null
  highlighted?: boolean
  /** 編集ボタン（鉛筆アイコン）を非表示にする */
  hideEditAction?: boolean
  /** 「呼出中にする」「作業中にする」等のステータス変更系ボタンを非表示にする */
  hideStatusAction?: boolean
  callingAction?: CallingAction | null
  onCardClick?: () => void
  onStatusChange?: (bookingKey: string, nextStatus: number) => void
  onEditClick?: (bookingKey: string) => void
  cardRef?: (el: HTMLDivElement | null) => void
}

/** 人物アイコン（アウトライン） */
const PersonIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2m0-2C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4zm-6 4c.22-.72 3.31-2 6-2 2.7 0 5.8 1.29 6 2H6z" />
  </svg>
)

const PhoneIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.54 5c.06.89.21 1.76.45 2.59l-1.2 1.2c-.41-1.2-.67-2.47-.76-3.79h1.51m9.86 12.02c.85.24 1.72.39 2.6.45v1.49c-1.32-.09-2.59-.35-3.8-.75l1.2-1.19M7.5 3H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.49c0-.55-.45-1-1-1-1.24 0-2.45-.2-3.57-.57-.1-.04-.21-.05-.31-.05-.26 0-.51.1-.71.29l-2.2 2.2c-2.83-1.45-5.15-3.76-6.59-6.59l2.2-2.2c.28-.28.36-.67.25-1.02C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1z" />
  </svg>
)

const MailIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z" />
  </svg>
)

/** 編集アイコン（鉛筆） */
const EditIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
)

const FieldRow: React.FC<{ label: string; value: string | number | null | undefined }> = ({
  label,
  value,
}) => {
  if (value == null || value === '') return null
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </div>
  )
}

export const BookingDetailCard: React.FC<Props> = ({
  booking,
  bookingKey,
  masterData,
  highlighted,
  hideEditAction,
  hideStatusAction,
  callingAction,
  onCardClick,
  onStatusChange,
  onEditClick,
  cardRef,
}) => {
  const { onClickFileDownload } = useAttachmentFileDownload()
  const { onClickFileDownload: onClickDriverFileDownload } = useAttachmentFileDownload(true)

  const status = booking.status ?? BOOKING_STATUS.Booking
  const nextAction = CHANGE_STATUS_BUTTON[status]

  const importFlag = booking.importFlag === 1
  const exportFlag = booking.exportFlag === 1

  const activeAttachments = (booking.attachments ?? []).filter((a) => !a.deletedAt)
  const activeDriverAttachments = (booking.attachmentsForDriver ?? []).filter((a) => !a.deletedAt)
  const hasAttachments = activeAttachments.length > 0

  // 入場指示日時: 指示なしチェック時は文言、それ以外は日時を表示（管理者備考の上に配置）
  const entryInstructionDisplay = booking.skipInstructionTime
    ? '指示を受けていない'
    : booking.entryInstructionTime
      ? booking.entryInstructionTime.replace(/-/g, '/')
      : undefined

  const durationMinutes = calcDurationMinutes(booking)

  const cardClassName = [styles.card, highlighted ? styles.cardHighlighted : '']
    .filter(Boolean)
    .join(' ')

  return (
    <div className={cardClassName} ref={cardRef} onClick={onCardClick}>
      {/* ===== 時間帯 + ステータスチップ ===== */}
      <div className={styles.topRow}>
        <div className={styles.timeRow}>
          <span className={styles.timeRange}>
            {(() => {
              const effectiveStart = booking.startTime ?? booking.planStartTime
              const effectiveEnd = booking.endTime ?? booking.planEndTime
              const [displayStart, displayEnd] = formatTimeRange(booking).split(' - ')
              return (
                <>
                  <span className={effectiveStart ? undefined : styles.timeEmpty}>
                    {displayStart}
                  </span>
                  {' - '}
                  <span className={effectiveEnd ? undefined : styles.timeEmpty}>{displayEnd}</span>
                </>
              )
            })()}
          </span>
          {durationMinutes != null && (
            <span className={styles.duration}>（{durationMinutes}分）</span>
          )}
          <div style={{ marginLeft: 4, flexShrink: 0 }}>
            <LoadingAndUnLoadingBadge isUnloadActive={importFlag} isLoadActive={exportFlag} />
          </div>
          {booking.generalBookingId != null && (
            <span className={styles.generalBadge} style={{ marginLeft: 8 }}>
              総
            </span>
          )}
        </div>
        <div className={styles.topRowRight}>
          <StatusChip status={status} />
          {onEditClick &&
            !hideEditAction &&
            canEditBookingByStatus(booking) &&
            bookingKey &&
            booking.bookingId && (
              <button
                type="button"
                className={styles.editIconButton}
                onClick={(e) => {
                  e.stopPropagation()
                  onEditClick(bookingKey)
                }}
                aria-label="編集"
              >
                <EditIcon />
              </button>
            )}
        </div>
      </div>

      {/* ===== 運送会社・ドライバー情報 ===== */}
      <div className={booking.driverCompanyName ? styles.companyName : styles.companyNameEmpty}>
        {booking.driverCompanyName || '運送会社が未設定です'}
      </div>

      <div className={styles.driverRow}>
        <span className={styles.contactGroup}>
          <span className={styles.contactIcon}>
            <PersonIcon />
          </span>
          <span className={styles.driverText}>{booking.driverName || '-'}</span>
        </span>
        <span className={styles.contactGroup}>
          <span className={styles.contactIcon}>
            <PhoneIcon />
          </span>
          <span className={styles.contactText}>
            {booking.driverPhone ? formatPhoneNumber(booking.driverPhone) : '-'}
          </span>
        </span>
        <span className={styles.contactGroup}>
          <span className={styles.contactIcon}>
            <MailIcon />
          </span>
          <span className={styles.contactText}>{booking.driverEmail || '-'}</span>
        </span>
      </div>

      {/* ===== セクション本体 ===== */}
      <div className={styles.sectionBody}>
        {/* 予約受付番号・車両情報 */}
        {(booking.bookingNo ||
          booking.vehicleNumber ||
          booking.vehicleCategory ||
          activeDriverAttachments.length > 0) && (
          <>
            <div className={styles.sectionGroup}>
              <div className={styles.fieldList}>
                <FieldRow label="予約受付番号" value={booking.bookingNo} />
                <FieldRow label="車両情報" value={booking.vehicleNumber} />
                <FieldRow
                  label="車両形態"
                  value={
                    masterData?.vehicleCategories.find(
                      (c) => String(c.parameterId) === String(booking.vehicleCategory)
                    )?.parameterName ?? undefined
                  }
                />
                {activeDriverAttachments.length > 0 && (
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>ドライバー添付資料</span>
                    <div className={styles.fileLinkList}>
                      {activeDriverAttachments.map((a) =>
                        canPreviewFileAttachment(a.name) ? (
                          <a
                            key={a.id}
                            className={styles.fileLink}
                            href={`${PREVIEW_FILE_ROUTES.driver}/${a.id}/${extractExtensionOfFileName(a.name)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {a.name}
                          </a>
                        ) : (
                          <button
                            key={a.id}
                            type="button"
                            className={styles.fileLink}
                            onClick={(e) => onClickDriverFileDownload(e, a)}
                          >
                            {a.name}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* 配車担当 */}
        {(booking.companyName || booking.vehicleControllerName) && (
          <>
            <div className={styles.divider} />
            <div className={styles.sectionGroup}>
              <div className={styles.categoryLabel}>配車担当</div>
              <div className={styles.fieldList}>
                <FieldRow label="会社名" value={booking.companyName} />
                <FieldRow label="配車担当" value={booking.vehicleControllerName} />
                <FieldRow
                  label="電話番号"
                  value={
                    booking.vehicleControllerPhone
                      ? formatPhoneNumber(booking.vehicleControllerPhone)
                      : undefined
                  }
                />
                <FieldRow label="メールアドレス" value={booking.vehicleControllerEmail} />
              </div>
            </div>
          </>
        )}

        {/* 荷降ろし */}
        {importFlag && (
          <>
            <div className={styles.divider} />
            <div className={styles.sectionGroup}>
              <div className={styles.categoryLabel}>荷降ろし</div>
              <div className={styles.fieldList}>
                {getSortedImportExportFields(
                  masterData?.tenantParameters?.import ?? [],
                  booking,
                  masterData
                ).map((f) => (
                  <FieldRow key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            </div>
          </>
        )}

        {/* 荷積み */}
        {exportFlag && (
          <>
            <div className={styles.divider} />
            <div className={styles.sectionGroup}>
              <div className={styles.categoryLabel}>荷積み</div>
              <div className={styles.fieldList}>
                {getSortedImportExportFields(
                  masterData?.tenantParameters?.export ?? [],
                  booking,
                  masterData
                ).map((f) => (
                  <FieldRow key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            </div>
          </>
        )}

        {/* 添付資料 */}
        {hasAttachments && (
          <>
            <div className={styles.divider} />
            <div className={styles.sectionGroup}>
              <div className={styles.categoryLabel}>添付資料</div>
              <div className={styles.fileLinkList}>
                {activeAttachments.map((a) =>
                  canPreviewFileAttachment(a.name) ? (
                    <a
                      key={a.id}
                      className={styles.fileLink}
                      href={`${PREVIEW_FILE_ROUTES.normal}/${a.id}/${extractExtensionOfFileName(a.name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className={`${determineIconOfFileName(a.name)} ${styles.fileIcon}`} />
                      {a.name}
                    </a>
                  ) : (
                    <button
                      key={a.id}
                      type="button"
                      className={styles.fileLink}
                      onClick={(e) => onClickFileDownload(e, a)}
                    >
                      <i className={`${determineIconOfFileName(a.name)} ${styles.fileIcon}`} />
                      {a.name}
                    </button>
                  )
                )}
              </div>
            </div>
          </>
        )}

        {/* 入場指示日時 / 管理者備考欄 */}
        {(entryInstructionDisplay || booking.adminNote) && (
          <>
            <div className={styles.divider} />
            <div className={styles.sectionGroup}>
              <FieldRow label="入場指示" value={entryInstructionDisplay} />
              <FieldRow label="管理者備考欄" value={booking.adminNote} />
            </div>
          </>
        )}

        {/* 退場時入力項目 */}
        {booking.checkoutAdditionalFields &&
          booking.checkoutAdditionalFields.filter((f) => f.textValue).length > 0 && (
            <>
              <div className={styles.divider} />
              <div className={styles.sectionGroup}>
                <div className={styles.categoryLabel}>退場時入力項目</div>
                <div className={styles.fieldList}>
                  {booking.checkoutAdditionalFields
                    .filter((f) => f.textValue)
                    .map((f) => (
                      <FieldRow key={f.fieldId} label={f.displayName} value={f.textValue} />
                    ))}
                </div>
              </div>
            </>
          )}
      </div>

      {/* ===== ステータス変更ボタン ===== */}
      {!hideStatusAction && callingAction && (
        <>
          <div className={styles.divider} style={{ marginTop: 12 }} />
          <button
            className={`${styles.actionButton} ${styles.btnArrival}`}
            disabled={callingAction.disabled}
            onClick={(e) => {
              e.stopPropagation()
              callingAction.onClick()
            }}
          >
            {callingAction.label}
          </button>
        </>
      )}
      {!hideStatusAction && !callingAction && nextAction && (
        <>
          <div className={styles.divider} style={{ marginTop: 12 }} />
          <button
            className={`${styles.actionButton} ${nextAction.className}`}
            onClick={(e) => {
              e.stopPropagation()
              if (onStatusChange && bookingKey) {
                onStatusChange(bookingKey, nextAction.value)
              }
            }}
          >
            {nextAction.label}
          </button>
        </>
      )}
    </div>
  )
}
